import {
  AssessmentSignatureUser,
  MeProviderQuery,
  PatientDataFragment,
  SignatureUser,
} from '../../../../../generated/graphql';
import { logger } from '../../../../../lib/logger';

export const createProgramAssessmentSignatureUsers = (
  assessmentSignatureUsers?: AssessmentSignatureUser[],
  sendingProviderUser?: MeProviderQuery['meProvider'] | null,
  patient?: PatientDataFragment,
) => {
  if (!assessmentSignatureUsers || !sendingProviderUser || !patient) {
    return [];
  }

  const assignedProviderUsers = patient.assignedProviderUsers.filter(
    (assignedProviderUser) =>
      assignedProviderUser.id !== sendingProviderUser.id,
  );

  const programAssessmentSignatureUsers = assessmentSignatureUsers.map(
    (assessmentSignatureUser) => {
      return {
        assessmentSignatureUserId: assessmentSignatureUser.id,
        ...(assessmentSignatureUser.signatureUserType ===
          SignatureUser.PatientUser && {
          name: patient.name,
        }),
        ...(assessmentSignatureUser.signatureUserType ===
          SignatureUser.SendingProviderUser && {
          providerUserId: sendingProviderUser.id,
          name: sendingProviderUser.name,
        }),
        ...(assessmentSignatureUser.signatureUserType ===
          SignatureUser.AssignedProviderUser && {
          providerUserId: assignedProviderUsers[0].id,
          name: assignedProviderUsers[0].name,
        }),
      };
    },
  );

  return programAssessmentSignatureUsers;
};

/**
 * Finds an available provider user, preferring the last selected one if available
 */
export const findAvailableProviderUser = (
  commonAssignedProviderUsers: Array<{ id: string; name: string }>,
  availableProviderUserIds: string[] | Set<string>, // Accept both
  lastSelectedTeamMemberId: string | null,
  excludeIds: string[] = [],
): { id: string; name: string } | undefined => {
  // Convert Set to Array if needed
  const idsArray = Array.isArray(availableProviderUserIds)
    ? availableProviderUserIds
    : Array.from(availableProviderUserIds);

  // Prefer last selected team member if available and not excluded
  if (
    lastSelectedTeamMemberId &&
    idsArray.includes(lastSelectedTeamMemberId) &&
    !excludeIds.includes(lastSelectedTeamMemberId)
  ) {
    return commonAssignedProviderUsers.find(
      (user) => user.id === lastSelectedTeamMemberId,
    );
  }

  // Otherwise, find first available provider user that's not excluded
  return commonAssignedProviderUsers.find(
    (user) => idsArray.includes(user.id) && !excludeIds.includes(user.id),
  );
};

export const getCommonAssignedProviderUsers = (
  patients: PatientDataFragment[],
  sendingProviderUser: any,
): Array<{ id: string; name: string }> => {
  if (patients.length === 0) {
    return [];
  }

  // Check for duplicate patient IDs (indicates data fetching issue)
  const uniquePatientIds = new Set(patients.map((p) => p.programInstanceId));
  if (uniquePatientIds.size !== patients.length) {
    logger.error(
      new Error(
        'Duplicate patient IDs detected in getCommonAssignedProviderUsers',
      ),
      {
        patientIds: patients.map((p) => p.programInstanceId),
        patientNames: patients.map((p) => `${p.firstName} ${p.lastName}`),
      },
    );
    // If there are duplicates, we can't determine common providers correctly
    // Return empty array to show error state
    return [];
  }

  if (patients.length === 1) {
    return (patients[0]?.assignedProviderUsers || [])
      .filter((user) => user.name) // Filter out users without names
      .map((user) => ({ id: user.id, name: user.name! }));
  }

  // Get all provider user IDs per patient
  const providerUserIdsPerPatient = patients.map((patient) =>
    (patient.assignedProviderUsers || []).map((user) => user.id),
  );

  // If any patient has no assigned provider users, there can be no common ones
  if (providerUserIdsPerPatient.some((ids) => ids.length === 0)) {
    return [];
  }

  // Find intersection of assigned provider users across all patients
  // Start with the first patient's IDs and filter by subsequent patients
  const firstPatientIds = providerUserIdsPerPatient[0];
  const commonProviderUserIds = providerUserIdsPerPatient
    .slice(1)
    .reduce(
      (acc, ids) => acc.filter((id) => ids.includes(id)),
      firstPatientIds,
    );

  // Get full provider user objects from the first patient (they all have the same structure)
  // Filter to ensure name is present and map to the required return type
  const commonProviderUsers = (patients[0]?.assignedProviderUsers || [])
    .filter((user) => commonProviderUserIds.includes(user.id) && user.name)
    .map((user) => ({ id: user.id, name: user.name! }));

  return commonProviderUsers;
};

export const checkNotEnoughAssignedProviderUsers = (
  assessmentSignatureUsers: AssessmentSignatureUser[],
  patient: PatientDataFragment | undefined,
  patients: PatientDataFragment[] | undefined,
  sendingProviderUser: any,
): boolean => {
  // Support both single patient (backward compatibility) and multiple patients
  const patientsList = patients || (patient ? [patient] : []);

  if (patientsList.length === 0) {
    return false;
  }

  const commonAssignedProviderUsers = getCommonAssignedProviderUsers(
    patientsList,
    sendingProviderUser,
  );

  const assignedProviderUsersCount = commonAssignedProviderUsers.length;
  const assignedProviderUserTypesCount =
    assessmentSignatureUsers?.filter(
      (user) => user.signatureUserType === SignatureUser.AssignedProviderUser,
    ).length || 0;

  const isSendingProviderUserIncluded = assessmentSignatureUsers?.some(
    (user) => user.signatureUserType === SignatureUser.SendingProviderUser,
  );

  const isSendingProviderUserAssigned = commonAssignedProviderUsers.some(
    (user) => user.id === sendingProviderUser?.id,
  );

  const effectiveAssignedProviderUsersCount = isSendingProviderUserIncluded
    ? assignedProviderUsersCount - (isSendingProviderUserAssigned ? 1 : 0)
    : assignedProviderUsersCount;

  return effectiveAssignedProviderUsersCount < assignedProviderUserTypesCount;
};

export const initializeSelectedAssignedProviderUsers = (
  assessmentSignatureUsers: AssessmentSignatureUser[] | undefined,
  sendingProviderUser: MeProviderQuery['meProvider'] | null,
  patient: PatientDataFragment | undefined,
  patients: PatientDataFragment[] | undefined,
  setSelectedAssignedProviderUsers: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >,
  lastSelectedTeamMemberId: string | null = null,
  availableProviderUserIds?: Set<string>,
) => {
  // Support both single patient (backward compatibility) and multiple patients
  const patientsList = patients || (patient ? [patient] : []);

  if (!assessmentSignatureUsers || patientsList.length === 0) {
    setSelectedAssignedProviderUsers({});
    return;
  }

  // Get common assigned provider users across all patients
  const commonAssignedProviderUsers = getCommonAssignedProviderUsers(
    patientsList,
    sendingProviderUser,
  );

  let availableAssignedProviderUserIds = commonAssignedProviderUsers.map(
    (user) => user.id,
  );
  const selectedAssignedProviderUsers: { [key: string]: string } = {};

  // Track if we've used the last selected team member for this activity
  let hasUsedLastSelected = false;

  for (const assessmentSignatureUser of assessmentSignatureUsers) {
    if (
      assessmentSignatureUser.signatureUserType ===
        SignatureUser.SendingProviderUser &&
      sendingProviderUser
    ) {
      selectedAssignedProviderUsers[assessmentSignatureUser.id] =
        sendingProviderUser.id;
      // Remove sending provider user from available list (if they're also assigned)
      availableAssignedProviderUserIds =
        availableAssignedProviderUserIds.filter(
          (id) => id !== sendingProviderUser.id,
        );
    } else if (
      assessmentSignatureUser.signatureUserType ===
        SignatureUser.AssignedProviderUser &&
      availableAssignedProviderUserIds.length > 0
    ) {
      // For the FIRST Team Member slot, use last selected team member if available
      // For subsequent Team Member slots, use next available team members
      const excludeIds = Object.values(selectedAssignedProviderUsers);
      const providerUserToUse = findAvailableProviderUser(
        commonAssignedProviderUsers,
        availableAssignedProviderUserIds,
        !hasUsedLastSelected ? lastSelectedTeamMemberId : null,
        excludeIds,
      );

      if (providerUserToUse) {
        if (providerUserToUse.id === lastSelectedTeamMemberId) {
          hasUsedLastSelected = true;
        }
        selectedAssignedProviderUsers[assessmentSignatureUser.id] =
          providerUserToUse.id;
        availableAssignedProviderUserIds =
          availableAssignedProviderUserIds.filter(
            (id) => id !== providerUserToUse.id,
          );
      }
    }
  }

  setSelectedAssignedProviderUsers(selectedAssignedProviderUsers);
};
